import axios from "axios";

import Logger from "shared/services/Logger.ts";

const FILE_SERVER_RETRY_DELAYS_MS = [250, 500, 1000, 2000];
const FILE_SERVER_RETRY_TIMEOUT_MS = 15_000;
const FILE_SERVER_DEPLOYMENT_MISMATCH_ERROR_NAME =
  "FileServerDeploymentMismatchError";

type RequestHeaders = Record<string, string>;
type AxiosGet = typeof axios.get;
type FileServerIndex = {
  _meta?: {
    deploymentId?: unknown;
  };
  [resourceName: string]: unknown;
};

function getResponseStatus(error: unknown): number | null {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof error.response === "object" &&
    error.response !== null &&
    "status" in error.response &&
    typeof error.response.status === "number"
  ) {
    return error.response.status;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof error.status === "number"
  ) {
    return error.status;
  }

  return null;
}

function hasExpectedDeploymentId(
  data: FileServerIndex | null,
  expectedDeploymentId: string
): boolean {
  if (!expectedDeploymentId) {
    return true;
  }

  return getDeploymentIdFromIndex(data) === expectedDeploymentId;
}

function getDeploymentIdFromIndex(data: FileServerIndex | null): string | null {
  if (!data || typeof data._meta !== "object" || data._meta === null) {
    return null;
  }

  if (
    "deploymentId" in data._meta &&
    typeof data._meta.deploymentId === "string"
  ) {
    return data._meta.deploymentId;
  }

  return null;
}

function shouldRetryFileServerRequest(
  error: unknown,
  expectedDeploymentId: string
): boolean {
  if (
    expectedDeploymentId &&
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === FILE_SERVER_DEPLOYMENT_MISMATCH_ERROR_NAME
  ) {
    return true;
  }

  const status = getResponseStatus(error);
  if (status === null) {
    return false;
  }

  return status === 404 || status === 409 || status >= 500;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function createFileServerIndexUrl(
  baseUrl: string,
  domainForFileServer: string,
  language: string,
  deploymentId: string
): string {
  if (!deploymentId) {
    return `${baseUrl}${domainForFileServer}/${language}/index.json`;
  }

  return `${baseUrl}${domainForFileServer}/${language}/index-${deploymentId}.json`;
}

export async function fetchFileServerData(
  urlToAsk: string,
  headers: RequestHeaders = {},
  expectedDeploymentId = "",
  requestFn: AxiosGet = axios.get,
  sleepFn: (ms: number) => Promise<void> = sleep
): Promise<FileServerIndex | null> {
  const startedAt = Date.now();

  for (let attempt = 0; ; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop -- retries are sequential by design.
      const response = await requestFn<FileServerIndex>(urlToAsk, { headers });
      const { data } = response;

      if (
        expectedDeploymentId &&
        !hasExpectedDeploymentId(data, expectedDeploymentId)
      ) {
        const mismatchError = new Error(
          `Expected deploymentId ${expectedDeploymentId}, got ${
            getDeploymentIdFromIndex(data) || "none"
          }`
        );
        mismatchError.name = FILE_SERVER_DEPLOYMENT_MISMATCH_ERROR_NAME;
        throw mismatchError;
      }

      return data;
    } catch (error) {
      const delayMs =
        attempt < FILE_SERVER_RETRY_DELAYS_MS.length
          ? FILE_SERVER_RETRY_DELAYS_MS[attempt]
          : FILE_SERVER_RETRY_DELAYS_MS[FILE_SERVER_RETRY_DELAYS_MS.length - 1];
      const timedOut = Date.now() - startedAt >= FILE_SERVER_RETRY_TIMEOUT_MS;

      if (
        timedOut ||
        delayMs === undefined ||
        !shouldRetryFileServerRequest(error, expectedDeploymentId)
      ) {
        Logger.error("File server index request failed", {
          cause: error instanceof Error ? error : undefined,
          requestEndpoint: "file-server-index",
          tags: { area: "file-server" },
          extra: {
            requestUrl: urlToAsk,
            expectedDeploymentId,
            responseStatus: getResponseStatus(error),
          },
        });
        return null;
      }

      // eslint-disable-next-line no-await-in-loop -- backoff must finish before retrying.
      await sleepFn(delayMs);
    }
  }
}
